import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import ToolRecommendationCards, {
  type ToolRecommendation,
} from '@/components/prompt-builder/ToolRecommendationCards';
import { TOOL_COLORS } from '@/lib/toolConstants';

interface WorkflowToolSelectorProps {
  recommendations: ToolRecommendation[];
  selectedTool: string | null;
  isOptimizing: boolean;
  onSelectTool: (rec: ToolRecommendation) => void;
}

export function WorkflowToolSelector({
  recommendations,
  selectedTool,
  isOptimizing,
  onSelectTool,
}: WorkflowToolSelectorProps) {
  const [showCards, setShowCards] = useState(!selectedTool);

  // Compact selected-tool bar
  if (!showCards && selectedTool) {
    const toolColor = TOOL_COLORS[selectedTool] || 'border-border bg-muted/20';
    return (
      <div className="fade-in-up">
        <div className={cn('flex items-center justify-between rounded-xl border-2 px-4 py-3', toolColor)}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">
              All prompts optimized for <span className="font-semibold">{selectedTool}</span>
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-1.5"
            onClick={() => setShowCards(true)}
            disabled={isOptimizing}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Change tool
          </Button>
        </div>
      </div>
    );
  }

  // Full recommendation cards
  return (
    <div className="space-y-3 fade-in-up">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-display font-semibold text-base text-foreground">
          Best AI Tool for This Workflow
        </h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Select a tool — all step prompts will be optimized for it.
      </p>
      <ToolRecommendationCards
        recommendations={recommendations}
        onSelect={(rec) => {
          onSelectTool(rec);
          setShowCards(false);
        }}
        isOptimizing={isOptimizing}
      />
      {isOptimizing && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Optimizing all prompts...
        </div>
      )}
    </div>
  );
}
